import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { updateCheckpoint } from '../../store/actions/checkpoint.actions';
import {
    getFormCheckpointByIdFactory,
    getFormCheckpointDependencies,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormInfoClass,
    getFormParticipantCopyReadonly,
    getFormRadioButtonLabels,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormType,
    getFormTypeFilters,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { CheckpointDependencies } from '../../types/checkpoints';
import { ContentId } from '../../types/content-block';
import { FormFilters, FormFiltersType } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../../types/functional-element';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import { isAnyFilterActive, isAstoriaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import FORMTYPE = FormsConstants.FORMTYPE;
import getFilterLabel = FormUtilities.getFilterLabel;

@Component({
    selector: 'pace-checkpoint',
    templateUrl: './checkpoint.component.html',
    styleUrls: ['./checkpoint.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckpointComponent implements OnInit, OnDestroy {
    @Input()
    public checkpoint: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public radioGroup: FormGroup;
    public radioControl: FormControl;

    public infoClass$: Observable<FormsConstants.INFOCLASS>;
    public readOnly$: Observable<boolean>;
    public radioButtonLabels: { [key: string]: string } = {};
    public checkpoint$: Observable<Checkpoint>;
    public checkpointTypeLabel$: Observable<string>;
    public options$: Observable<RadioOption[]>;
    public selectedOption$: Observable<RadioOption | undefined>;
    public dependencies$: Observable<CheckpointDependencies>;
    public functionalElement$: Observable<FunctionalElement | undefined>;
    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public visible$: Observable<boolean>;
    private selectedParticipant$: Observable<ParticipantCopyId>;

    private destroyTrigger$: Subject<void>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
    ) {
        this.destroyTrigger$ = new Subject<void>();
        this.infoClass$ = this.store.select(getFormInfoClass);
        this.dependencies$ = this.store.select(getFormCheckpointDependencies);
        this.typeFilters$ = this.store.select(getFormTypeFilters);
        this.formFilters$ = this.store.select(getFormFilters);
        this.selectedParticipant$ = this.store.select(getFormSelectedParticipantCopyId);
        this.store
            .select(getFormRadioButtonLabels)
            .pipe(
                distinctUntilChanged(
                    (oldLabels, newLabels) =>
                        oldLabels.yes === newLabels.yes &&
                        oldLabels.no === newLabels.no &&
                        oldLabels.na === newLabels.na,
                ),
                takeUntil(this.destroyTrigger$),
            )
            .subscribe((labels) => {
                this.radioButtonLabels = {
                    [RADIO_OPTION_TYPE.YES]: labels.yes,
                    [RADIO_OPTION_TYPE.NO]: labels.no,
                    [RADIO_OPTION_TYPE.NA]: labels.na,
                };
            });
    }

    public ngOnInit(): void {
        this.radioControl = this.formBuilder.control(null);
        this.radioGroup = this.formBuilder.group({
            radioControl: this.radioControl,
        });
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.radioGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'CheckpointComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    ),
                );
        }

        this.checkpoint$ = this.store
            .select(getFormCheckpointByIdFactory({ id: this.checkpoint }))
            .pipe(filter(isDefined));

        this.options$ = this.checkpoint$.pipe(
            switchMap((checkpoint) =>
                this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId })),
            ),
            filter(isDefined),
            shareReplay(1),
        );
        this.selectedOption$ = this.options$.pipe(
            map((options) => options.find((o) => o.selected)),
            distinctUntilChanged((o1, o2) => o1?.optionType === o2?.optionType),
        );

        this.readOnly$ = combineLatest([
            this.store.select(getFormType),
            this.store.select(getFormParticipantCopyReadonly),
            this.options$,
            this.selectedOption$,
            this.infoClass$,
        ]).pipe(
            map(
                ([formType, readOnly, options, selectedOption, infoClass]) =>
                    readOnly ||
                    // Check if all the options are disabled (which should be the usual case)
                    options.map((o) => o.disabled).reduce((accu, curr) => accu && curr, true) ||
                    // In training checklists, options that were set to "yes" in previous session are no longer allowed
                    // to be edited.
                    (formType === FORMTYPE.TRAININGCHECKLIST &&
                        infoClass === FormsConstants.INFOCLASS.APPLICATION &&
                        typeof selectedOption !== 'undefined' &&
                        selectedOption.contentLoadedFromXml &&
                        selectedOption.optionType === RADIO_OPTION_TYPE.YES),
            ),
        );

        this.functionalElement$ = combineLatest([this.checkpoint$, this.dependencies$]).pipe(
            switchMap(([checkpoint, dependencies]) => {
                const functionalElement = dependencies[checkpoint.xmlId];
                if (typeof functionalElement === 'undefined') {
                    return of(undefined);
                }
                const functionalElementId = functionalElement.functionalElement;
                return this.store.pipe(select(getFormFunctionalElementByIdFactory({ id: functionalElementId })));
            }),
        );

        this.visible$ = combineLatest([
            this.checkpoint$,
            this.options$,
            this.functionalElement$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([checkpoint, options, functionalElement, typeFilters, formFilters, selectedParticipant]) => {
                // find the option that was selected in the master
                const selectedInMaster = options.find((o) => o.selectedInMaster);
                const loadedFromXml = options.some((o) => o.contentLoadedFromXml);
                const yesInMaster =
                    typeof selectedInMaster !== 'undefined' && selectedInMaster.optionType === RADIO_OPTION_TYPE.YES;

                // Check if there is a dependent functional element and if it is a signature
                const hasDependentSignature =
                    typeof functionalElement !== 'undefined' &&
                    (functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                        functionalElement.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE);

                let visibleByFilter = !isAnyFilterActive(typeFilters, formFilters);
                if (typeof functionalElement === 'undefined') {
                    const typeFilterVisible = typeFilters[checkpoint.elementType].visible;

                    const selectedOption = options.find((o) => o.selected);
                    // standalone checkpoint is completed as soon as an option is selected
                    const showIncompleteElement =
                        formFilters[FormFiltersType.INCOMPLETE_ELEMENTS].visible && !isDefined(selectedOption);
                    const showNotOkElement =
                        formFilters[FormFiltersType.NOTOK_ELEMENTS].visible &&
                        isDefined(selectedOption) &&
                        selectedOption.optionType === RADIO_OPTION_TYPE.NO;

                    visibleByFilter = visibleByFilter || typeFilterVisible || showIncompleteElement || showNotOkElement;
                } else {
                    visibleByFilter =
                        visibleByFilter ||
                        isAstoriaFunctionalElementVisible(
                            functionalElement,
                            checkpoint.elementType,
                            typeFilters,
                            formFilters,
                            { [checkpoint.xmlId]: options },
                        );
                }

                return (
                    visibleByFilter &&
                    // The master displays everything.
                    (selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID ||
                        // Do not hide the checkpoint in a participant copy if it controls a signature
                        hasDependentSignature ||
                        // Participant copies only show content that was not loaded from an interim XML and that was
                        // answered with YES in the master.
                        (!loadedFromXml && yesInMaster))
                );
            }),
        );

        this.checkpointTypeLabel$ = combineLatest([this.checkpoint$, this.store.select(getFormTypeFilters)]).pipe(
            map(([c, filters]) => getFilterLabel(c.elementType, filters)),
        );

        this.selectedOption$
            .pipe(withLatestFrom(this.readOnly$), takeUntil(this.destroyTrigger$))
            .subscribe(([selectedOption, readOnly]) => {
                // If the selection is different than the previous selection for this component, then we need to
                // patch the value in the UI
                if (typeof selectedOption !== 'undefined') {
                    this.radioControl?.patchValue(selectedOption.optionType, { emitEvent: false });
                } else {
                    // if the selectedOption is undefined, the we need to ensure that the UI does not have any selection
                    this.radioControl.reset({}, { emitEvent: false });
                }
                this.radioControl[readOnly ? 'disable' : 'enable']({ emitEvent: false });
            });

        this.radioControl.valueChanges
            .pipe(withLatestFrom(this.options$, this.readOnly$), takeUntil(this.destroyTrigger$))
            .subscribe(([value, options, readOnly]: [RADIO_OPTION_TYPE | null, RadioOption[], boolean]) => {
                // if the control is disabled, don't do anything. It can still happen that the value is changed when
                // a different participant copy is loaded.
                if (readOnly) {
                    return;
                }

                const xmlId = options[0].xmlId;

                const selected = value === null ? undefined : value;

                const selectedOptionInState = options.find((o) => o.selected);
                // this needs to be taken out of the state because we might have switched participant copies and then
                // the previousSelection store in the component might not correctly reflect the actual previous
                // selection for that participant copy
                const selectedInState =
                    typeof selectedOptionInState !== 'undefined' ? selectedOptionInState.optionType : undefined;

                if (selected === selectedInState) {
                    return;
                }
                this.store.dispatch(
                    updateCheckpoint({
                        xmlId,
                        selected,
                    }),
                );
            });

        this.readOnly$
            .pipe(takeUntil(this.destroyTrigger$))
            .subscribe((readOnly) => this.radioControl[readOnly ? 'disable' : 'enable']({ emitEvent: false }));
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.radioGroup);
            this.parentForm.removeAt(index);
        }

        this.destroyTrigger$.next();
    }

    public reset(): void {
        this.radioControl.reset();
    }

    public trackByFn(index: number, item: RadioOption): string {
        return item.xmlId;
    }
}
